import { useT } from '../i18n';
import './Stepper.css';

interface StepperProps {
  current: number;
  total: number;
  labels: string[];
  /** Steps already completed are navigable; steps ahead are not. */
  onNavigate: (step: number) => void;
}

export function Stepper({ current, total, labels, onNavigate }: StepperProps) {
  const t = useT();

  return (
    <nav className="stepper" aria-label={t.a11y.stepperLabel}>
      <ol className="stepper__list">
        {labels.map((label, index) => {
          const step = index + 1;
          const state = step < current ? 'done' : step === current ? 'current' : 'todo';
          const navigable = step < current;

          return (
            <li key={label} className={`stepper__item stepper__item--${state}`}>
              {navigable ? (
                <button type="button" className="stepper__button" onClick={() => onNavigate(step)}>
                  <StepBody index={step} label={label} state={state} />
                </button>
              ) : (
                <span className="stepper__button" aria-current={state === 'current' ? 'step' : undefined}>
                  <StepBody index={step} label={label} state={state} />
                </span>
              )}
            </li>
          );
        })}
      </ol>
      <p className="stepper__count">{t.app.stepOf(current, total)}</p>
    </nav>
  );
}

function StepBody({ index, label, state }: { index: number; label: string; state: string }) {
  const t = useT();
  return (
    <>
      <span className="stepper__index" aria-hidden="true">
        {String(index).padStart(2, '0')}
      </span>
      <span className="stepper__label">{label}</span>
      {/* The visual state is carried by rule weight and ink; spell it out for
          anyone who cannot see either. */}
      {state !== 'todo' && (
        <span className="visually-hidden">
          {state === 'done' ? t.a11y.stepComplete : t.a11y.stepCurrent}
        </span>
      )}
    </>
  );
}
